import LegalPageLayout from './LegalPageLayout';
import {
	LegalContact,
	LegalEffectiveDate,
	LegalList,
	LegalParagraph,
	LegalSection,
} from './LegalSection';

const Privacy = () => (
	<LegalPageLayout title="Privacy Policy">
		<LegalEffectiveDate />

		<LegalParagraph>
			This Privacy Policy describes how Bidzbase (&quot;we,&quot;
			&quot;us,&quot; or &quot;our&quot;) collects, uses, and shares information
			when you use bidzbase.com and related applications (the
			&quot;Service&quot;).
		</LegalParagraph>

		<LegalSection title="1. Who this policy applies to">
			<LegalParagraph>
				This policy applies to users who create accounts and use the Service on
				behalf of a business (&quot;Organization&quot;). It also describes how
				we process information that Organizations enter about their customers,
				employees, and jobs.
			</LegalParagraph>
			<LegalParagraph>
				When an Organization stores personal information about its customers or
				employees in Bidzbase, the Organization is generally the business
				responsible for that data. We process it on the Organization&apos;s
				instructions to provide the Service.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="2. Information we collect">
			<LegalParagraph>
				<strong>Account and Organization information</strong> — name, email
				address, phone number, password (stored hashed), Organization name, role, and
				billing-related identifiers from Stripe.
			</LegalParagraph>
			<LegalParagraph>
				<strong>Business content you submit</strong> — customer names and
				contact details, job and bid data, line items, notes, uploaded files,
				branding assets (such as logos), and payment-related settings.
			</LegalParagraph>
			<LegalParagraph>
				<strong>Communications metadata</strong> — records of transactional
				emails sent through the Service (for example, proposals and invoices),
				delivery status, and support correspondence.
			</LegalParagraph>
			<LegalParagraph>
				<strong>Usage and technical data</strong> — IP address, browser type,
				device information, log data, and error reports collected to operate,
				secure, and debug the Service.
			</LegalParagraph>
			<LegalParagraph>
				<strong>Sample demo requests</strong> — email address (and optional
				name) when you request a sample document from our public website.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="3. How we use information">
			<LegalList
				items={[
					'Provide, maintain, and improve the Service.',
					'Authenticate users and enforce plan limits.',
					"Generate and deliver PDFs, emails, and payment links on your Organization's behalf.",
					'Process subscriptions and billing through Stripe.',
					'Monitor performance, prevent abuse (including one free trial per phone number), and investigate security incidents.',
					'Respond to support requests and communicate about the Service.',
					'Comply with legal obligations.',
				]}
			/>
			<LegalParagraph>
				We do not sell personal information. We do not use Organization customer
				data for advertising or unrelated marketing.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="4. How we share information">
			<LegalParagraph>
				We share information with service providers that help us run the
				Service, and only as needed for them to perform their work:
			</LegalParagraph>
			<LegalList
				items={[
					'Stripe — subscription billing and, when enabled by your Organization, Stripe Connect payment processing.',
					'SendGrid — transactional email delivery.',
					'Amazon Web Services (S3) — file storage for uploads such as logos, photos, and documents.',
					'Sentry — error monitoring and application diagnostics.',
				]}
			/>
			<LegalParagraph>
				We may also disclose information if required by law, to protect rights
				and safety, or in connection with a merger, acquisition, or sale of
				assets.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="5. Cookies and similar technologies">
			<LegalParagraph>
				We use essential cookies and similar technologies to keep you signed in
				and to operate the Service. We do not currently use advertising or
				third-party analytics cookies on the marketing site.
			</LegalParagraph>
			<LegalParagraph>
				You can control cookies through your browser settings, but disabling
				essential cookies may prevent you from using authenticated parts of the
				Service.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="6. Data retention">
			<LegalParagraph>
				We retain Organization data while your subscription is active. After
				cancellation or termination, we retain data for thirty (30) days to
				allow export, then delete it unless we must retain certain records for
				legal, security, or billing purposes.
			</LegalParagraph>
			<LegalParagraph>
				We retain signup phone numbers indefinitely to enforce a one-free-trial-per-phone
				limit, even if the related account is later deactivated. We do not use
				those numbers for advertising.
			</LegalParagraph>
			<LegalParagraph>
				Sample-demo request logs are retained only as long as needed for abuse
				prevention.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="7. Security">
			<LegalParagraph>
				We use reasonable administrative, technical, and organizational measures
				to protect information, including encryption in transit, access
				controls, and tenant isolation between Organizations. No method of
				transmission or storage is completely secure.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="8. SMS">
			<LegalParagraph>
				If your Organization uses SMS features, we process mobile phone numbers
				and message content to deliver internal, transactional notifications to
				an opted-in manager phone number. Managers opt in through the SMS alerts
				form in Settings (and as described on our public SMS opt-in page),
				including an affirmative consent checkbox that is not pre-selected.
				Message frequency varies and is event-driven. Msg &amp; data rates may
				apply. Recipients may reply STOP to cancel or HELP for help.
				Organizations must comply with applicable law, including TCPA
				requirements in the United States.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="9. Your choices and rights">
			<LegalParagraph>
				Organization administrators can access, update, and delete much of their
				data directly in the Service. You may contact us to request access,
				correction, or deletion of account information, subject to applicable
				law and our need to retain certain records.
			</LegalParagraph>
			<LegalParagraph>
				Depending on where you live, you may have additional privacy rights (for
				example, under California or other U.S. state privacy laws). We will
				honor valid requests in accordance with applicable law.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="10. International users">
			<LegalParagraph>
				Bidzbase is operated from the United States. If you access the Service
				from outside the U.S., you understand that information may be processed
				and stored in the U.S. and other countries where our service providers
				operate.
			</LegalParagraph>
		</LegalSection>

		<LegalSection title="11. Changes to this policy">
			<LegalParagraph>
				We may update this Privacy Policy from time to time. If changes are
				material, we will provide notice (for example, by email or in-app
				notice) before they take effect. The effective date at the top of this
				page will reflect the latest revision.
			</LegalParagraph>
		</LegalSection>

		<LegalContact subject="Privacy Policy inquiry" />
	</LegalPageLayout>
);

export default Privacy;
